import Routes from "components/Routes";
import { SnackbarProvider } from "notistack";
import "./App.scss";
import { useEffect, useMemo } from "react";
import { ensureHubspotLoaded } from "utils/hubspotTracking";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CmsColorModeProvider, useCmsColorMode } from "context/CmsColorModeContext";

const AppContent = () => {
  const { mode } = useCmsColorMode();

  const cmsTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: "#8446AD",
            dark: "#6B2C91",
            light: "#A855F7",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#A855F7",
            dark: "#8446AD",
            light: "#C99FFD",
            contrastText: "#ffffff",
          },
          background: {
            default: mode === "dark" ? "#090f1a" : "#f6f0fb",
            paper: mode === "dark" ? "#121b2b" : "#ffffff",
          },
          text: {
            primary: mode === "dark" ? "#e2e8f0" : "#0f172a",
            secondary: mode === "dark" ? "#a6b4cc" : "#475569",
          },
          divider:
            mode === "dark"
              ? "rgba(148, 163, 184, 0.28)"
              : "rgba(132, 70, 173, 0.24)",
          action: {
            hover:
              mode === "dark"
                ? "rgba(148, 163, 184, 0.12)"
                : "rgba(132, 70, 173, 0.08)",
            selected:
              mode === "dark"
                ? "rgba(192, 132, 252, 0.18)"
                : "rgba(132, 70, 173, 0.16)",
          },
        },
        typography: {
          fontFamily:
            "'Aeonik', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                background: "var(--cms-app-bg)",
                color: "var(--cms-text-primary)",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundImage: "none",
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundImage: "none",
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              head: {
                backgroundColor: "var(--cms-table-head-bg)",
                color: "var(--cms-text-primary)",
                borderColor: "var(--cms-border)",
              },
              body: {
                color: "var(--cms-text-primary)",
                borderColor: "var(--cms-border)",
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                backgroundColor: "var(--cms-input-bg)",
              },
            },
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    // Load HubSpot tracking for CMS so event creation can fire behavioral events.
    ensureHubspotLoaded();
  }, []);

  return (
    <ThemeProvider theme={cmsTheme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <div className="App">
          <Routes />
        </div>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <CmsColorModeProvider>
      <AppContent />
    </CmsColorModeProvider>
  );
}

export default App;
